// 各デコをパステル色の Three.js プリミティブの組み合わせで表現する。
//
// ドット絵テクスチャは作らず「形と色」で可愛さを出す（ピクセレートパイプラインが
// 効くので低ポリで良い）。色は palette を基調に、デコごとのアクセント色を足す。
// デコ 1 個はおよそ 1 セル（CELL_SIZE）に収まる大きさに作る。

import * as THREE from 'three';
import { PALETTE } from '../palette';
import { CELL_SIZE } from './diorama';
import { buildPlantMesh } from './plant_mesh';
import { isPlantDeco, type GrowthStage, type TreeForm } from '../sim/plants';

/** デコ固有のアクセント色（パレットに無い淡色はここで定義）。 */
const DECO_COLORS = {
  violet: '#C9B8E8',
  leafGreen: '#9CCB8A',
  crystalBlue: PALETTE.crystalBlue,
  crystalPink: '#F0C0D0',
  lanternRed: '#E89BA8',
  water: '#9DD6F0',
  bench: '#D8B89A',
  stone: '#CFC3DD',
  mushroomCap: '#F4A8B8',
  mushroomStem: '#F5E8D8',
  chimeGold: '#FFD79A',
  metal: '#BFC8D8',
  trunk: '#A07A5A',
} as const;

function mat(color: string, opts: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    roughness: 0.85,
    metalness: 0.0,
    flatShading: true,
    ...opts,
  });
}

/** 発光っぽく見せたい部位用（ライティングに依存しないベタ塗り）。 */
function glowMat(color: string): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color: new THREE.Color(color) });
}

// 各ビルダーはセル原点（中心）に立つ Group を返す。高さ 0 が地面。
type Builder = () => THREE.Group;

/**
 * 季節の結晶の共通形状。石の台座の上に季節色の大きな単晶を立て、
 * 中心に発光コアを仕込んでほのかに光らせる（夜に映える）。
 * body: 結晶本体の色 / core: 発光コアの色（本体より明るい同系色）。
 */
function buildSeasonCrystal(body: string, core: string): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.26, 0.1, 8), mat(DECO_COLORS.stone));
  base.position.y = 0.05;
  g.add(base);
  const inner = new THREE.Mesh(new THREE.OctahedronGeometry(0.1), glowMat(core));
  inner.scale.set(0.8, 2.0, 0.8);
  inner.position.y = 0.36;
  g.add(inner);
  const crystal = new THREE.Mesh(
    new THREE.OctahedronGeometry(0.15),
    mat(body, { roughness: 0.25, metalness: 0.1, transparent: true, opacity: 0.78 }),
  );
  crystal.scale.set(0.85, 2.1, 0.85);
  crystal.position.y = 0.36;
  g.add(crystal);
  for (const [x, z, tilt] of [
    [0.16, 0.06, 0.35],
    [-0.14, -0.05, -0.3],
  ] as const) {
    const kid = new THREE.Mesh(
      new THREE.OctahedronGeometry(0.07),
      mat(body, { roughness: 0.3, metalness: 0.1, transparent: true, opacity: 0.85 }),
    );
    kid.scale.set(0.8, 1.6, 0.8);
    kid.position.set(x, 0.18, z);
    kid.rotation.z = tilt;
    g.add(kid);
  }
  return g;
}

const builders: Record<string, Builder> = {
  // 宇宙すみれの花壇: 木枠の箱 + 上に小さな花の点々
  violet_bed() {
    const g = new THREE.Group();
    const box = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.18, 0.6), mat(DECO_COLORS.bench));
    box.position.y = 0.09;
    g.add(box);
    const soil = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.06, 0.52), mat(DECO_COLORS.trunk));
    soil.position.y = 0.2;
    g.add(soil);
    const flowerColors = [DECO_COLORS.violet, DECO_COLORS.crystalPink, DECO_COLORS.chimeGold];
    for (let i = 0; i < 5; i++) {
      const a = i * 1.4;
      const f = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 5), mat(flowerColors[i % 3]));
      f.position.set(Math.cos(a) * 0.16, 0.28, Math.sin(a) * 0.16);
      g.add(f);
    }
    return g;
  },

  // クリスタル群晶: 大小の尖った八面体クラスタ
  crystal_cluster() {
    const g = new THREE.Group();
    const specs: [number, number, number, number, string][] = [
      [0, 0, 0.42, 0, DECO_COLORS.crystalBlue],
      [0.14, 0.08, 0.28, 0.3, DECO_COLORS.crystalPink],
      [-0.14, 0.05, 0.26, -0.4, DECO_COLORS.crystalBlue],
      [0.04, -0.1, 0.2, 0.8, '#C9B8E8'],
    ];
    for (const [x, z, h, tilt, c] of specs) {
      const crystal = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.12),
        mat(c, { roughness: 0.3, metalness: 0.1, transparent: true, opacity: 0.9 }),
      );
      crystal.scale.set(0.8, h / 0.24, 0.8);
      crystal.position.set(x, h / 2, z);
      crystal.rotation.z = tilt;
      g.add(crystal);
    }
    return g;
  },

  // 季節の結晶: 台座 + 季節色の大きな単晶（中心がほのかに発光）+ 小さな子晶
  crystal_spring: () => buildSeasonCrystal('#F2B8CC', '#FBE3EC'),
  crystal_summer: () => buildSeasonCrystal('#8FD8A8', '#D8F5E0'),
  crystal_autumn: () => buildSeasonCrystal('#F0A868', '#FAD9B8'),
  crystal_winter: () => buildSeasonCrystal('#A8CFF0', '#E0F2FF'),

  // ランタン: 細い柱 + 発光する笠
  lantern() {
    const g = new THREE.Group();
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.45, 6), mat(DECO_COLORS.metal));
    post.position.y = 0.22;
    g.add(post);
    const light = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.2, 0.16), glowMat(PALETTE.warmOrange));
    light.position.y = 0.5;
    g.add(light);
    const cap = new THREE.Mesh(new THREE.ConeGeometry(0.13, 0.1, 4), mat(DECO_COLORS.lanternRed));
    cap.position.y = 0.64;
    cap.rotation.y = Math.PI / 4;
    g.add(cap);
    return g;
  },

  // 小さな池: 低い縁の円 + 水面
  pond() {
    const g = new THREE.Group();
    const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.08, 16), mat(DECO_COLORS.stone));
    rim.position.y = 0.04;
    g.add(rim);
    const water = new THREE.Mesh(
      new THREE.CylinderGeometry(0.28, 0.28, 0.06, 16),
      mat(DECO_COLORS.water, { roughness: 0.2, metalness: 0.2 }),
    );
    water.position.y = 0.07;
    g.add(water);
    return g;
  },

  // ベンチ: 座面 + 脚 + 背もたれ
  bench() {
    const g = new THREE.Group();
    const seat = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.05, 0.22), mat(DECO_COLORS.bench));
    seat.position.y = 0.2;
    g.add(seat);
    const back = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.18, 0.04), mat(DECO_COLORS.bench));
    back.position.set(0, 0.3, -0.09);
    g.add(back);
    for (const sx of [-0.25, 0.25]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.2, 0.18), mat(DECO_COLORS.trunk));
      leg.position.set(sx, 0.1, 0);
      g.add(leg);
    }
    return g;
  },

  // 石畳タイル: 薄い平板（無属性・通路）
  stone_tile() {
    const g = new THREE.Group();
    const tile = new THREE.Mesh(new THREE.BoxGeometry(0.74, 0.04, 0.74), mat(DECO_COLORS.stone));
    tile.position.y = 0.02;
    g.add(tile);
    return g;
  },

  // 光るキノコ: 茎 + 発光する笠を数本
  glow_mushroom() {
    const g = new THREE.Group();
    const specs: [number, number, number][] = [
      [0, 0, 1.0],
      [0.18, 0.12, 0.7],
      [-0.16, -0.1, 0.6],
    ];
    for (const [x, z, s] of specs) {
      const stem = new THREE.Mesh(
        new THREE.CylinderGeometry(0.04 * s, 0.05 * s, 0.22 * s, 6),
        mat(DECO_COLORS.mushroomStem),
      );
      stem.position.set(x, 0.11 * s, z);
      g.add(stem);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.1 * s, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2), glowMat(DECO_COLORS.mushroomCap));
      cap.position.set(x, 0.22 * s, z);
      g.add(cap);
    }
    return g;
  },

  // 風鈴ポール: 柱 + 横木 + 揺れる風鈴
  wind_chime() {
    const g = new THREE.Group();
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.6, 6), mat(DECO_COLORS.trunk));
    post.position.y = 0.3;
    g.add(post);
    const arm = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.03, 0.03), mat(DECO_COLORS.trunk));
    arm.position.set(0.12, 0.58, 0);
    g.add(arm);
    const chime = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), glowMat(DECO_COLORS.chimeGold));
    chime.position.set(0.24, 0.46, 0);
    g.add(chime);
    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.04, 0.1, 0.04), mat(DECO_COLORS.crystalPink));
    tail.position.set(0.24, 0.36, 0);
    g.add(tail);
    return g;
  },

  // 噴水: 段重ねの水盤 + 上に吹き上がる水
  fountain() {
    const g = new THREE.Group();
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.32, 0.34, 0.1, 12), mat(DECO_COLORS.stone));
    base.position.y = 0.05;
    g.add(base);
    const bowl = new THREE.Mesh(new THREE.CylinderGeometry(0.2, 0.16, 0.08, 12), mat(DECO_COLORS.stone));
    bowl.position.y = 0.2;
    g.add(bowl);
    const water = new THREE.Mesh(
      new THREE.CylinderGeometry(0.04, 0.06, 0.3, 8),
      mat(DECO_COLORS.water, { roughness: 0.2, transparent: true, opacity: 0.85 }),
    );
    water.position.y = 0.38;
    g.add(water);
    const top = new THREE.Mesh(new THREE.SphereGeometry(0.08, 8, 6), mat(DECO_COLORS.water, { transparent: true, opacity: 0.8 }));
    top.position.y = 0.54;
    g.add(top);
    return g;
  },

  // テーブル席（v0.7.0）: 丸テーブル＋脚＋卓上の小さなランプ（calm+light の趣）。
  cafe_table() {
    const g = new THREE.Group();
    const top = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.04, 14), mat(DECO_COLORS.bench));
    top.position.y = 0.3;
    g.add(top);
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.035, 0.05, 0.3, 8), mat(DECO_COLORS.metal));
    post.position.y = 0.15;
    g.add(post);
    const foot = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.16, 0.03, 10), mat(DECO_COLORS.metal));
    foot.position.y = 0.015;
    g.add(foot);
    // 卓上ランプ（ほのかに灯る）
    const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 6), glowMat(PALETTE.warmOrange));
    lamp.position.set(0.08, 0.4, 0.04);
    g.add(lamp);
    // ちいさなカップ（席らしさ）
    const cup = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.03, 0.05, 8), mat('#F5E8D8'));
    cup.position.set(-0.08, 0.345, -0.02);
    g.add(cup);
    return g;
  },

  // 入口ゲート（v0.7.2 で大型化＋「入口」と一目で分かる造形に）: 鳥居/門に似た背の高い
  // ゲート。2 本の太い柱＋見切りの笠木＋上の貫（横木）で「門」の輪郭を強く出し、貫の中央に
  // 発光サイン板、足元に「ここから来る」を示す下向きの光る矢印を置く。撤去不可・回転無効。
  entrance() {
    const g = new THREE.Group();
    const pillarMat = mat('#CBBEE0'); // ラベンダーの柱
    const beamMat = glowMat('#BFE3F5'); // 笠木・貫（ふんわり発光）
    const GATE_W = 0.34; // 柱の左右間隔（半幅）
    const GATE_H = 0.95; // 門の高さ（背を高くして「門」と分かる）

    // 太い 2 本柱＋足元の礎石。
    for (const sx of [-GATE_W, GATE_W]) {
      const pillar = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, GATE_H, 8), pillarMat);
      pillar.position.set(sx, GATE_H / 2, 0);
      g.add(pillar);
      const footing = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.13, 0.08, 8), mat(DECO_COLORS.stone));
      footing.position.set(sx, 0.04, 0);
      g.add(footing);
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 6), glowMat(PALETTE.crystalBlue));
      cap.position.set(sx, GATE_H + 0.05, 0);
      g.add(cap);
    }
    // 笠木（鳥居の一番上の横木、両端が少しせり出す）。
    const kasagi = new THREE.Mesh(new THREE.BoxGeometry(GATE_W * 2 + 0.32, 0.09, 0.14), beamMat);
    kasagi.position.set(0, GATE_H + 0.02, 0);
    g.add(kasagi);
    // 貫（笠木の下の横木）。サイン板の土台。
    const nuki = new THREE.Mesh(new THREE.BoxGeometry(GATE_W * 2 + 0.14, 0.07, 0.1), pillarMat);
    nuki.position.set(0, GATE_H * 0.74, 0);
    g.add(nuki);
    // 中央の発光サイン板（「入口」の額）。正面（+Z）を向く。
    const sign = new THREE.Mesh(
      new THREE.PlaneGeometry(0.28, 0.16),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(PALETTE.cafeWindowGlowCore), transparent: true, opacity: 0.95, side: THREE.DoubleSide }),
    );
    sign.position.set(0, GATE_H * 0.86, 0.07);
    g.add(sign);
    const signFrame = new THREE.Mesh(new THREE.TorusGeometry(0.16, 0.018, 6, 4), glowMat(PALETTE.warmOrange));
    signFrame.position.set(0, GATE_H * 0.86, 0.06);
    signFrame.rotation.z = Math.PI / 4;
    signFrame.scale.set(1.0, 0.62, 1);
    g.add(signFrame);

    // ゲート内のワープ膜（淡い発光円板、半透明）。
    const veil = new THREE.Mesh(
      new THREE.CircleGeometry(0.3, 16),
      new THREE.MeshBasicMaterial({ color: new THREE.Color('#D9C6F0'), transparent: true, opacity: 0.42, side: THREE.DoubleSide }),
    );
    veil.position.set(0, GATE_H * 0.42, 0);
    g.add(veil);

    // 足元の光るプレート（着地点）。
    const plate = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.03, 16), glowMat('#E6DAF6'));
    plate.position.y = 0.015;
    (plate.material as THREE.MeshBasicMaterial).transparent = true;
    (plate.material as THREE.MeshBasicMaterial).opacity = 0.75;
    g.add(plate);

    // 「ここから来る」を示す下向きの光る矢印（プレートの上に浮かべる）。
    const arrow = new THREE.Mesh(new THREE.ConeGeometry(0.11, 0.16, 4), glowMat(PALETTE.warmOrange));
    arrow.position.set(0, 0.2, 0.0);
    arrow.rotation.x = Math.PI; // 下向き
    arrow.rotation.y = Math.PI / 4;
    g.add(arrow);

    return g;
  },

  // 若木: 幹 + 丸い樹冠（成長は M4。今は 1 段階）
  sapling() {
    const g = new THREE.Group();
    const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 0.3, 6), mat(DECO_COLORS.trunk));
    trunk.position.y = 0.15;
    g.add(trunk);
    const crown = new THREE.Mesh(new THREE.IcosahedronGeometry(0.22, 0), mat(DECO_COLORS.leafGreen));
    crown.position.y = 0.4;
    g.add(crown);
    const crown2 = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14, 0), mat('#B5DDA0'));
    crown2.position.set(0.1, 0.34, 0.06);
    g.add(crown2);
    return g;
  },
};

/**
 * 植物デコの描画コンテキスト（成長段階＋樹形）。garden_view が plantings から渡す。
 */
export interface PlantRender {
  stage: GrowthStage;
  form: TreeForm;
}

/**
 * デコ ID からそのデコの 3D 表現（Group）を作る。
 *
 * 植物デコ（sapling / violet_bed）は plant を渡すと成長段階・樹形を反映した姿になる
 * （plant 省略時は静的ビルダーで「若木の標準形」を描く＝ゴーストやデモのフォールバック）。
 * 未知 ID には小さなプレースホルダ（灰色の箱）を返す（落ちない）。
 * 返る Group はセル中心原点・地面 y=0 基準。スケールはおよそ CELL_SIZE 内。
 */
export function buildDecoMesh(decoId: string, plant?: PlantRender): THREE.Group {
  // 植物デコ: 成長コンテキストがあれば段階・樹形を反映した姿を作る。
  if (isPlantDeco(decoId) && plant) {
    const tree = buildPlantMesh(decoId, plant.stage, plant.form);
    if (tree) return tree;
  }
  const builder = builders[decoId];
  if (builder) return builder();
  const g = new THREE.Group();
  const box = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.3, 0.3), mat('#999999'));
  box.position.y = 0.15;
  g.add(box);
  return g;
}

/** 1 デコの占有幅（参考値）。配置のスケール調整に使える。 */
export const DECO_FOOTPRINT = CELL_SIZE;
